import { useCallback, useState, useRef } from 'react';
import { Header, flexRender } from '@tanstack/react-table';
import {
  ArrowUp,
  ArrowDown,
  Filter,
  EyeOff,
  Pencil,
  Trash2,
  Plus,
  GripVertical,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface GridHeaderProps<TData> {
  header: Header<TData, unknown>;
  onSort?: (columnId: string, direction: 'asc' | 'desc' | null) => void;
  onFilter?: (columnId: string) => void;
  onHide?: (columnId: string) => void;
  onRename?: (columnId: string, newName: string) => void;
  onDelete?: (columnId: string) => void;
  onInsertLeft?: (columnId: string) => void;
  onInsertRight?: (columnId: string) => void;
  sortDirection?: 'asc' | 'desc' | null;
  hasFilter?: boolean;
  isResizing?: boolean;
}

export function GridHeader<TData>({
  header,
  onSort,
  onFilter,
  onHide,
  onRename,
  onDelete,
  onInsertLeft,
  onInsertRight,
  sortDirection,
  hasFilter = false,
  isResizing = false,
}: GridHeaderProps<TData>) {
  const [isRenaming, setIsRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleStartRename = useCallback(() => {
    setNewName(header.column.columnDef.header as string || '');
    setIsRenaming(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [header.column.columnDef.header]);

  const handleSaveRename = useCallback(() => {
    if (newName.trim() && onRename) {
      onRename(header.column.id, newName.trim());
    }
    setIsRenaming(false);
  }, [newName, onRename, header.column.id]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        handleSaveRename();
      } else if (e.key === 'Escape') {
        setIsRenaming(false);
      }
    },
    [handleSaveRename]
  );

  const handleSortClick = useCallback(() => {
    if (!onSort) return;

    if (sortDirection === 'asc') {
      onSort(header.column.id, 'desc');
    } else if (sortDirection === 'desc') {
      onSort(header.column.id, null);
    } else {
      onSort(header.column.id, 'asc');
    }
  }, [onSort, sortDirection, header.column.id]);

  return (
    <div
      className={cn(
        'relative flex items-center h-9 border-r border-b border-gray-200 bg-gray-50',
        'group select-none',
        isResizing && 'bg-blue-50'
      )}
      style={{ width: header.getSize() }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        const draggedId = e.dataTransfer.getData('text/column-id');
        if (draggedId && draggedId !== header.column.id) {
          // Column reorder will be handled by parent
        }
      }}
    >
      {/* Column drag handle */}
      <div
        className="w-4 h-full flex items-center justify-center cursor-grab opacity-0 group-hover:opacity-100"
        draggable
        onDragStart={(e) => {
          e.dataTransfer.setData('text/column-id', header.column.id);
          e.dataTransfer.effectAllowed = 'move';
        }}
      >
        <GripVertical className="h-3 w-3 text-gray-400" />
      </div>

      {/* Column title */}
      <div className="flex-1 min-w-0 px-2 flex items-center gap-1">
        {isRenaming ? (
          <input
            ref={inputRef}
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onBlur={handleSaveRename}
            onKeyDown={handleKeyDown}
            className="w-full px-1 py-0.5 text-sm border rounded outline-none focus:ring-1 focus:ring-blue-500"
          />
        ) : (
          <span
            className="text-sm font-medium text-gray-700 truncate cursor-pointer"
            onClick={handleSortClick}
          >
            {header.isPlaceholder
              ? null
              : flexRender(header.column.columnDef.header, header.getContext())}
          </span>
        )}

        {/* Sort indicator */}
        {sortDirection && (
          <span className="flex-shrink-0">
            {sortDirection === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5 text-blue-500" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 text-blue-500" />
            )}
          </span>
        )}

        {/* Filter indicator */}
        {hasFilter && (
          <Filter className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" />
        )}
      </div>

      {/* Column menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="p-1 mr-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity">
            <svg
              className="h-4 w-4 text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => onSort?.(header.column.id, 'asc')}>
            <ArrowUp className="h-4 w-4 mr-2" />
            Sort A to Z
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onSort?.(header.column.id, 'desc')}>
            <ArrowDown className="h-4 w-4 mr-2" />
            Sort Z to A
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onFilter?.(header.column.id)}>
            <Filter className="h-4 w-4 mr-2" />
            Filter by this column
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleStartRename}>
            <Pencil className="h-4 w-4 mr-2" />
            Rename column
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onHide?.(header.column.id)}>
            <EyeOff className="h-4 w-4 mr-2" />
            Hide column
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => onInsertLeft?.(header.column.id)}>
            <Plus className="h-4 w-4 mr-2" />
            Insert column left
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onInsertRight?.(header.column.id)}>
            <Plus className="h-4 w-4 mr-2" />
            Insert column right
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-red-600"
            onClick={() => onDelete?.(header.column.id)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete column
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Resize handle */}
      <div
        className={cn(
          'absolute right-0 top-0 h-full w-1 cursor-col-resize',
          'hover:bg-blue-500 transition-colors',
          isResizing && 'bg-blue-500'
        )}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
      />
    </div>
  );
}

// Header row component that renders all headers
interface GridHeaderRowProps<TData> {
  headers: Header<TData, unknown>[];
  onSort?: (columnId: string, direction: 'asc' | 'desc' | null) => void;
  onFilter?: (columnId: string) => void;
  onHide?: (columnId: string) => void;
  onRename?: (columnId: string, newName: string) => void;
  onDelete?: (columnId: string) => void;
  onInsertLeft?: (columnId: string) => void;
  onInsertRight?: (columnId: string) => void;
  onAddColumn?: () => void;
  sortState?: Record<string, 'asc' | 'desc'>;
  filterState?: Record<string, unknown>;
}

export function GridHeaderRow<TData>({
  headers,
  onSort,
  onFilter,
  onHide,
  onRename,
  onDelete,
  onInsertLeft,
  onInsertRight,
  onAddColumn,
  sortState = {},
  filterState = {},
}: GridHeaderRowProps<TData>) {
  return (
    <div className="flex items-stretch sticky top-0 z-10 bg-gray-50">
      {/* Drag handle column — frozen */}
      <div className="w-6 border-r border-b border-gray-200 bg-gray-50 sticky left-0 z-20" />

      {/* Row number header — frozen */}
      <div className="w-[50px] flex items-center justify-center text-xs font-medium text-gray-500 border-r border-b border-gray-200 bg-gray-50 sticky left-6 z-20">
        #
      </div>

      {/* Checkbox header — frozen */}
      <div className="w-10 flex items-center justify-center border-r border-b border-gray-200 bg-gray-50 sticky left-[74px] z-20">
        {/* Master checkbox could go here */}
      </div>

      {/* Column headers */}
      {headers.map((header) => (
        <GridHeader
          key={header.id}
          header={header}
          onSort={onSort}
          onFilter={onFilter}
          onHide={onHide}
          onRename={onRename}
          onDelete={onDelete}
          onInsertLeft={onInsertLeft}
          onInsertRight={onInsertRight}
          sortDirection={sortState[header.column.id]}
          hasFilter={!!filterState[header.column.id]}
          isResizing={header.column.getIsResizing()}
        />
      ))}

      {/* Add column button */}
      <div className="w-10 flex items-center justify-center border-b border-gray-200 bg-gray-50">
        <button
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          onClick={onAddColumn}
          aria-label="Add column"
        >
          <Plus className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {/* Actions column header (empty) */}
      <div className="w-8 border-b border-gray-200 bg-gray-50" />
    </div>
  );
}

export default GridHeader;
